import { type Page, typedApi } from "@/api/typed-client";
import { useQuery } from "@tanstack/react-query";

export type DatasetStatus =
  | "profiling"
  | "materializing"
  | "ready"
  | "failed"
  | "cancelled"
  | "archived";

export interface DatasetListItem {
  id: string;
  name: string;
  kind: string;
  status: DatasetStatus;
  rows: number | null;
  cols: number | null;
  size_bytes: number | null;
  created_at: string;
}

export interface DatasetDetail {
  id: string;
  name: string;
  kind: string;
  status: DatasetStatus;
  row_count: number | null;
  column_count: number | null;
  size_bytes: number | null;
  created_at: string;
  last_accessed_at: string | null;
  schema: Array<{
    name: string;
    dtype: string;
    nullable: boolean;
    distinct?: number;
    mean?: number;
  }>;
}

export interface DatasetSample {
  columns: Array<{ name: string; dtype: string }>;
  rows: Array<Record<string, unknown>>;
  row_count: number;
  truncated: boolean;
}

export function useDatasets(
  projectId: string | null,
  params: { page?: number; pageSize?: number } = {},
) {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;
  return useQuery({
    enabled: !!projectId,
    queryKey: ["datasets", projectId, page, pageSize],
    queryFn: async () => {
      const { data, error } = await typedApi.GET("/v1/datasets", {
        params: { query: { project_id: projectId ?? "", page, page_size: pageSize } },
      });
      if (error) throw new Error(JSON.stringify(error));
      // Justified: Page_DatasetListItem_ schema is structurally compatible but lacks size_bytes field
      return data as unknown as Page<DatasetListItem>;
    },
    placeholderData: (prev) => prev,
  });
}

export function useDataset(id: string | null) {
  return useQuery({
    enabled: !!id,
    queryKey: ["dataset", id],
    queryFn: async () => {
      const { data, error } = await typedApi.GET("/v1/datasets/{dataset_id}", {
        params: { path: { dataset_id: id ?? "" } },
      });
      if (error) throw new Error(JSON.stringify(error));
      // Justified: DatasetDetail schema has schema[] as {[key:string]:unknown} not typed column entries
      return data as unknown as DatasetDetail;
    },
  });
}

export function useDatasetSample(id: string | null) {
  return useQuery({
    enabled: !!id,
    queryKey: ["dataset", id, "sample"],
    queryFn: async () => {
      const { data, error } = await typedApi.GET("/v1/datasets/{dataset_id}/sample", {
        params: { path: { dataset_id: id ?? "" } },
      });
      if (error) throw new Error(JSON.stringify(error));
      // Justified: sample endpoint returns {[key:string]:unknown} in openapi schema; actual shape has sample field
      const r = data as unknown as { sample: DatasetSample };
      return r.sample;
    },
  });
}
